import { CloseOutlined, MoreOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useRequest } from 'ahooks';
import { Dropdown, Form, Input, List, Modal, Popover, Space } from 'antd';
import useNotification from 'antd/es/notification/useNotification';
import IconRound from 'assets/logos/icon-round.svg?react';
import { Button } from 'components/ui';
import { colors, palette } from 'design';
import useSelectConversations from 'hooks/useSelectConversations';
import posthog from 'posthog-js';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import ApiMessageTemplatesManager from 'services/api/ApiMessageTemplatesManager';
import ApiPatientConversationsManager from 'services/api/ApiPatientConversationsManager';
import { MessageTemplateDTO } from 'services/api/models/MessageTemplateDTO';
import { conversationActions } from 'store/conversations';
import { selectCurrentUser } from 'store/users/selectors';
const { TextArea } = Input;

export type MessageInputProps = {
  disabled?: boolean;
  animatePlaceholder?: boolean;
  minRows?: number;
  onMessageSent?: () => void;
  containerStyle?: React.CSSProperties;
  /** Removes the left spacer and right margin so the input fits flush inside a card */
  compact?: boolean;
};

const useStyles = createUseStyles({
  container: {
    zIndex: 9,
    padding: 13,
  },
  messageInput: {
    marginInlineEnd: '2px',
  },
  plusButton: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  sendButton: {
    display: 'flex',
    alignItems: 'flex-end',
    '& .ant-btn-icon': {
      marginLeft: 2,
    },
  },
  templatesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    '& h3': {
      margin: 0,
    },
  },
  templatesList: {
    paddingLeft: 8,
    scrollbarWidth: 'none',
    '& .ant-list-item': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  replyPreviewContainer: {
    width: '100%',
    padding: '0px 0px 13px 13px',
  },
  replyPreview: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    backgroundColor: colors.surface.page,
    borderLeft: `5px solid ${palette.mint[600]}`,
    borderRadius: 4,

    '& .reply-content': {
      flex: 1,
      overflow: 'hidden',
    },
    '& .reply-sender': {
      fontSize: 13,
      fontWeight: 500,
      color: colors.brand.accentStrong,
      marginBottom: 2,
    },
    '& .reply-text': {
      fontSize: 12,
      color: colors.text.tertiary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
});

const MessageInput: React.FC<MessageInputProps> = ({
  disabled = false,
  animatePlaceholder = false,
  minRows = 1,
  onMessageSent,
  containerStyle,
  compact = false,
}) => {
  // State
  const [inputValue, setInputValue] = useState<string>();

  // Hooks
  const styles = useStyles();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [api, context] = useNotification();
  const dispatch = useDispatch();
  // Selector
  const {
    conversationId: currentConversationId,
    inputText: currentInputText,
    conversationMetadata,
    replyingToMessage,
  } = useSelectConversations();
  const currentUser = useSelector(selectCurrentUser);

  const saveInputTextValueInStore = (text?: string) => {
    dispatch(
      conversationActions.updateConversationInputText({
        conversationId: currentConversationId,
        inputText: text,
      }),
    );
  };

  const saveInputText = (text?: string) => {
    if (currentConversationId) {
      // Existing conversation, save the text in Redux store
      saveInputTextValueInStore(text);
    } else {
      // New conversation (= no conversation id), save the text in local state
      setInputValue(text);
    }
  };

  function sendMessage(messageText: string) {
    if (!currentConversationId) {
      return ApiPatientConversationsManager.createPatientConversations({
        initialMessageText: messageText,
      });
    }

    const repliedToMessageId = replyingToMessage?.messageId;

    if (currentUser?.is_caregiver) {
      return ApiDoctorConversationsManager.postMessageToDoctorConversation({
        conversationId: currentConversationId,
        messageText,
        repliedToMessageId,
      });
    }

    return ApiPatientConversationsManager.postMessageToPatientConversations({
      conversationId: currentConversationId,
      messageText,
      repliedToMessageId,
    });
  }

  const { isPending: loading, mutate: run } = useMutation({
    mutationFn: (messageText: string) => sendMessage(messageText),
    onSuccess: () => {
      form.resetFields();
      saveInputText(undefined);

      if (currentConversationId && replyingToMessage) {
        dispatch(
          conversationActions.setReplyingToMessage({
            conversationId: currentConversationId,
            replyingTo: undefined,
          }),
        );
      }
      // For new conversations (no currentConversationId), navigation is handled by
      // the conversation.created_by_patient WebSocket event in Shell.tsx so it fires even if
      // this component has already unmounted.
    },
    onError: () => {
      api.error({
        message: t('chats.list.errors.sendMessage'),
      });
    },
  });

  const onFinish = (values: any) => {
    posthog.capture('chats.message_sent');
    run(values.message);
    if (onMessageSent) {
      onMessageSent();
    }
  };

  // animate placeholders within the textarea
  const placeholders = t<string, { returnObjects: true }, string[]>('chats.new.exampleMessages', {
    returnObjects: true,
  });
  const [placeholder, setPlaceholder] = useState('');
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const currentPlaceholder = placeholders[currentPlaceholderIndex];
    function nextAnimationTick() {
      if (placeholder.length < currentPlaceholder.length) {
        setPlaceholder(currentPlaceholder.slice(0, placeholder.length + 1));
      } else {
        setCurrentPlaceholderIndex((currentPlaceholderIndex + 1) % placeholders.length);
        setPlaceholder('');
      }
    }

    if (animatePlaceholder) {
      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = setTimeout(nextAnimationTick, placeholder.length === currentPlaceholder.length ? 1200 : 20);
    }
  }, [placeholder, currentPlaceholderIndex]);

  // Get the input text, either from the Redux store or local state
  const input = currentInputText || inputValue;
  // A text is present and no empty text
  const isValidInputValue = !!(input && input.length > 0);

  // Templates (caregivers only)
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [newTemplateModalOpen, setNewTemplateModalOpen] = useState(false);
  const [editTemplateModalOpen, setEditTemplateModalOpen] = useState(false);
  const [editTemplateForm] = Form.useForm();
  const [templateBeingEdited, setTemplateBeingEdited] = useState<MessageTemplateDTO | null>(null);
  const [newTemplateForm] = Form.useForm();
  const {
    run: loadTemplates,
    loading: loadingTemplates,
    data: templates,
  } = useRequest(() => ApiMessageTemplatesManager.getMessageTemplates(), {
    cacheKey: 'message-templates',
    manual: true,
  });

  const { run: createTemplate, loading: creatingTemplate } = useRequest(
    (params: { name: string; message_text: string }) => ApiMessageTemplatesManager.createMessageTemplate(params),
    {
      manual: true,
      onSuccess: () => {
        // Refresh list and insert created template into the input
        loadTemplates();
        setNewTemplateModalOpen(false);
        newTemplateForm.resetFields();
      },
    },
  );

  const { run: updateTemplate, loading: updatingTemplate } = useRequest(
    (id: string, params: { name?: string; message_text?: string }) =>
      ApiMessageTemplatesManager.updateMessageTemplate(id, params),
    {
      manual: true,
      onSuccess: () => {
        loadTemplates();
        setEditTemplateModalOpen(false);
        setTemplateBeingEdited(null);
        editTemplateForm.resetFields();
      },
    },
  );

  const { run: deleteTemplate, loading: deletingTemplate } = useRequest(
    (id: string) => ApiMessageTemplatesManager.deleteMessageTemplate(id),
    {
      manual: true,
      onSuccess: () => {
        loadTemplates();
      },
    },
  );

  function onTemplatePicked(t: MessageTemplateDTO) {
    // Set both form and store/local state to the template text
    let text = t.message_text;
    if (currentConversationId && conversationMetadata?.conversation?.patient) {
      const patient = conversationMetadata.conversation.patient;
      if (patient?.first_name) {
        text = text.replace(/@PR[EÉ]NOM/gi, patient.first_name);
      }
      if (patient?.last_name) {
        text = text.replace(/@NOM/gi, patient.last_name);
      }
      if (currentUser?.first_name) {
        text = text.replace(/@MON_PR[EÉ]NOM/gi, currentUser.first_name);
      }
      if (currentUser?.last_name) {
        text = text.replace(/@MON_NOM/gi, currentUser.last_name);
      }
    }
    form.setFields([{ name: 'message', value: text }]);
    saveInputText(text);
    setTemplatesOpen(false);
  }

  useEffect(() => {
    if (currentUser?.is_caregiver) {
      loadTemplates();
    }
  }, []);

  return (
    <Form
      className={styles.container}
      layout="inline"
      form={form}
      onFinish={onFinish}
      style={{ paddingLeft: 0, ...containerStyle }}
      fields={[
        {
          name: ['message'],
          value: currentInputText || inputValue,
        },
      ]}
    >
      {context}
      {replyingToMessage && (
        <div className={styles.replyPreviewContainer}>
          <div className={styles.replyPreview}>
            <div className="reply-content">
              <div className="reply-sender">{replyingToMessage.senderName}</div>
              <div className="reply-text">{replyingToMessage.messageText}</div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              icon={<CloseOutlined />}
              onClick={() => {
                if (currentConversationId) {
                  dispatch(
                    conversationActions.setReplyingToMessage({
                      conversationId: currentConversationId,
                      replyingTo: undefined,
                    }),
                  );
                }
              }}
            />
          </div>
        </div>
      )}
      {!currentUser?.is_caregiver && !compact && <div style={{ width: 12 }}></div>}
      {currentUser?.is_caregiver && (
        <Form.Item className={styles.plusButton} style={{ margin: 0 }}>
          <Popover
            placement="topRight"
            trigger="click"
            open={templatesOpen}
            onOpenChange={open => {
              setTemplatesOpen(open);
              if (open) {
                posthog.capture('chats.caregiver-templates.opened');
              }
            }}
            content={
              <div
                className={styles.templatesList}
                style={{ minWidth: 280, maxWidth: 380, maxHeight: 320, overflowY: 'auto' }}
              >
                <div className={styles.templatesHeader}>
                  <h3>{t('chats.templates.title')}</h3>
                  <Button
                    variant="primary"
                    size="md"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setTemplatesOpen(false);
                      setNewTemplateModalOpen(true);
                      // Prefill message with current input for convenience
                      newTemplateForm.setFields([
                        { name: 'name', value: '' },
                        { name: 'message_text', value: input || '' },
                      ]);
                    }}
                  >
                    {t('common.new')}
                  </Button>
                </div>
                <List
                  size="small"
                  loading={loadingTemplates && (templates === undefined || templates.length === 0)}
                  locale={{ emptyText: t('common.empty') }}
                  dataSource={templates ?? []}
                  renderItem={item => {
                    const isOwner = item.created_by === currentUser?.id;
                    const menuItems = [
                      { key: 'edit', label: t('common.edit') },
                      { key: 'delete', label: t('common.delete') },
                    ];
                    return (
                      <List.Item
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          onTemplatePicked(item);
                          posthog.capture('chats.caregiver-templates.template-picked', {
                            template_id: item.id,
                            template_name: item.name,
                          });
                        }}
                        actions={
                          isOwner
                            ? [
                                <Dropdown
                                  key="more"
                                  menu={{
                                    items: menuItems,
                                    onClick: ({ key }) => {
                                      if (key === 'edit') {
                                        setTemplatesOpen(false);
                                        setTemplateBeingEdited(item);
                                        editTemplateForm.setFields([
                                          { name: 'name', value: item.name },
                                          { name: 'message_text', value: item.message_text },
                                        ]);
                                        setEditTemplateModalOpen(true);
                                      } else if (key === 'delete') {
                                        setTemplatesOpen(false);
                                        Modal.confirm({
                                          title: t('common.confirm'),
                                          content: t('chats.templates.confirmDelete'),
                                          okText: t('common.delete'),
                                          okButtonProps: { danger: true, loading: deletingTemplate },
                                          cancelText: t('common.cancel'),
                                          onOk: () => deleteTemplate(item.id),
                                        });
                                      }
                                    },
                                  }}
                                  trigger={['click']}
                                >
                                  <Button
                                    variant="link"
                                    size="md"
                                    icon={<MoreOutlined />}
                                    onClick={e => e.stopPropagation()}
                                  />
                                </Dropdown>,
                              ]
                            : undefined
                        }
                      >
                        <List.Item.Meta
                          title={
                            isOwner ? (
                              item.name
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <IconRound aria-hidden fill={colors.brand.accent} height={16} width={16} />
                                <span>{item.name}</span>
                              </span>
                            )
                          }
                          description={
                            <span style={{ whiteSpace: 'pre-wrap' }}>
                              {item.message_text.length > 120
                                ? `${item.message_text.slice(0, 120)}…`
                                : item.message_text}
                            </span>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              </div>
            }
          >
            <Button variant="link" size="lg" icon={<PlusOutlined />} shape="circle" />
          </Popover>
        </Form.Item>
      )}
      <Form.Item
        className={styles.messageInput}
        style={{ flex: 1 }}
        name="message"
        rules={[{ required: true, message: '' }]}
      >
        <TextArea
          placeholder={animatePlaceholder ? placeholder : t('chats.fields.message.placeholder')}
          size="large"
          autoComplete="off"
          autoCorrect="on"
          autoSize={{ minRows, maxRows: 5 }}
          onChange={e => saveInputText(e.target.value)}
          data-testid="message-input"
        />
      </Form.Item>

      <Form.Item className={styles.sendButton} style={{ margin: 0, marginLeft: 8 }}>
        <Space>
          <Button
            variant="primary"
            htmlType="submit"
            size="lg"
            icon={<SendOutlined />}
            disabled={loading || disabled || !isValidInputValue}
            loading={loading}
            shape="circle"
          />
        </Space>
      </Form.Item>
      <Modal
        title={t('chats.templates.editTemplate')}
        open={editTemplateModalOpen}
        onCancel={() => {
          setEditTemplateModalOpen(false);
          setTemplateBeingEdited(null);
        }}
        okText={t('common.save')}
        confirmLoading={updatingTemplate}
        onOk={() => {
          editTemplateForm
            .validateFields()
            .then(values => {
              if (templateBeingEdited) {
                updateTemplate(templateBeingEdited.id, { name: values.name, message_text: values.message_text });
              }
            })
            .catch(() => undefined);
        }}
      >
        <Form form={editTemplateForm} layout="vertical">
          <Form.Item name="name" label={t('chats.templates.fields.name')} rules={[{ required: true }]}>
            <Input maxLength={256} />
          </Form.Item>
          <Form.Item
            name="message_text"
            label={t('chats.templates.fields.messageText')}
            rules={[{ required: true }]}
            extra={t('chats.templates.helperTags')}
          >
            <TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={t('chats.templates.newTemplate')}
        open={newTemplateModalOpen}
        onCancel={() => setNewTemplateModalOpen(false)}
        okText={t('common.create')}
        confirmLoading={creatingTemplate}
        onOk={() => {
          newTemplateForm
            .validateFields()
            .then(values => {
              createTemplate({ name: values.name, message_text: values.message_text });
            })
            .catch(() => undefined);
        }}
      >
        <Form form={newTemplateForm} layout="vertical">
          <Form.Item name="name" label={t('chats.templates.fields.name')} rules={[{ required: true }]}>
            <Input maxLength={256} />
          </Form.Item>
          <Form.Item
            name="message_text"
            label={t('chats.templates.fields.messageText')}
            rules={[{ required: true }]}
            extra={t('chats.templates.helperTags')}
          >
            <TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
          </Form.Item>
        </Form>
      </Modal>
    </Form>
  );
};
export default MessageInput;
